import { BaseWebComponentView } from '../../framework/BaseWebComponentView';
import { Column, ColumnKeys, ImportantMessageGridItem, ImportantMessagesWebComponentSchema } from '../types';
import { WEB_COMPONENT_GRID_VIEW } from '../../webcomponents.constants';
import { GridViewWebComponent } from '../../grid/gridView';
import { ImportantMessagesGridDataProvider } from '../dataProvider/ImportantMessagesGridDataProvider';
import { GzButtonWebComponent } from '../../ui/gzButton';
import { ButtonType } from '../../ui/gzButton/types';

export class ImportantMessagesView extends BaseWebComponentView {
  private timeIntervalRenderer({ item }: { item: ImportantMessageGridItem }): HTMLElement {
    const dateOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    } as const;
    const activeIntervalCell = document.createElement('div');
    const { displayStartTime, displayEndTime } = item;

    const startDateString = new Date(displayStartTime).toLocaleString('ro', dateOptions);
    const endDateString = new Date(displayEndTime).toLocaleString('ro', dateOptions);

    activeIntervalCell.innerHTML =
      `${this.commonStrings.from} ${startDateString}` + `<br> ${this.commonStrings.to} ${endDateString}`;
    return activeIntervalCell;
  }

  private nameRenderer({ item }: { item: ImportantMessageGridItem }): HTMLElement {
    const nameCell = document.createElement('gz-button') as GzButtonWebComponent;
    nameCell.type = 'link' as ButtonType;

    nameCell.innerText = item.name;

    nameCell.addEventListener('click', function () {
      this.dispatchEvent(
        new CustomEvent('edit', {
          detail: { item },
          bubbles: true,
          composed: true,
        })
      );
    } as EventListener);
    return nameCell;
  }

  private enabledRenderer({ item }: { item: ImportantMessageGridItem }): HTMLElement {
    const yesNoCell: HTMLElement = document.createElement('div');
    const yesNoString: string = item.enabled ? 'Yes' : 'No';
    yesNoCell.innerText = this.dependencies.translateFn(yesNoString);

    return yesNoCell;
  }

  public disableAllModifyButtons(): void {
    this.dom.$enableButton.disabled = true;
    this.dom.$disableButton.disabled = true;
    this.dom.$deleteButton.disabled = true;
  }

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<
      Column,
      ColumnKeys,
      ImportantMessageGridItem
    >;
    this.dom.$controlsHeader = this.shadow.querySelector('.controls-header');
    this.dom.$addNewButton = this.shadow.querySelector('#addNewButton');
    this.dom.$enableButton = this.shadow.querySelector('#enableButton');
    this.dom.$disableButton = this.shadow.querySelector('#disableButton');
    this.dom.$deleteButton = this.shadow.querySelector('#deleteButton');
    this.disableAllModifyButtons();

    if (this.dom.$grid.injectDependencies) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start().then().catch(console.error);
    }
  }

  private addTextToButtons(): void {
    this.dom.$addNewButton.innerText = this.dependencies.translateFn('Add New');
    this.dom.$enableButton.innerText = this.dependencies.translateFn('Enable');
    this.dom.$disableButton.innerText = this.dependencies.translateFn('Disable');
    this.dom.$deleteButton.innerText = this.dependencies.translateFn('Delete');
  }

  buildSuccessView(data: ImportantMessagesWebComponentSchema): void {
    const view = data.views[0];

    // build the grid with data from the backend
    this.dom.$grid.propertiesSet = false;
    this.dom.$grid.dataProvider = new ImportantMessagesGridDataProvider(this.dependencies);

    this.dom.$grid.viewId = view.id;
    this.dom.$grid.allColumnDetails = data.allColumns;
    this.dom.$grid.columns = view.displayedColumns;
    this.dom.$grid.filters = view.filters;
    this.dom.$grid.isCompactViewChecked = false;
    this.dom.$grid.defaultViewProps = data.defaultViewProps;
    this.dom.$grid.pageSize = view.pageSize;
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.NAME]: this.nameRenderer,
      [ColumnKeys.ENABLED]: this.enabledRenderer.bind(this),
      [ColumnKeys.DISPLAY_START_TIME]: this.timeIntervalRenderer.bind(this),
    };

    this.addTextToButtons();
    this.disableAllModifyButtons();

    this.dom.$grid.propertiesSet = true;
  }

  buildErrorView(error: Error) {
    // uses default error view
  }
}
